import React, { useEffect, useState } from "react";
import { Zap, ScanLine, Mic, Trophy, Award, Crown, Activity } from "lucide-react";
import SkeletalHudMock from "../components/landing/SkeletalHudMock";

const RED = "#C41E24", GOLD = "#D4AF37";
const PILLARS = [
  { Icon: Zap, label: "Technical Engine", sub: "Every rep graded" },
  { Icon: ScanLine, label: "Skeletal Tracking", sub: "13-point scan" },
  { Icon: Mic, label: "Voice Feedback", sub: "Coach in your ear" },
  { Icon: Trophy, label: "Local Leaderboard", sub: "Central Illinois" },
  { Icon: Award, label: "Official Enrollment", sub: "Volume 1 certificate" },
];

// Brand-official 4:5 ad — rendered by scripts/render_ad.py at 1080×1350 (social) and 2400×3000 (300dpi print).
export default function PrintAd() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    Promise.all([document.fonts.ready, ...["/brand/nexto-academy-logo.png", "/marketing/hero-player.jpg"].map((s) => new Promise((r) => { const i = new Image(); i.onload = i.onerror = r; i.src = s; }))]).then(() => setReady(true));
  }, []);
  return (
    <div className="bg-black min-h-screen flex items-start justify-start">
      <div data-testid="print-ad" data-ready={ready ? "1" : "0"} className="relative overflow-hidden text-white elite-carbon-bg grain" style={{ width: 1080, height: 1350, fontFamily: "Montserrat, sans-serif" }}>
        {/* ambient light */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(700px 420px at 50% 0%, ${GOLD}33, transparent 70%), radial-gradient(900px 600px at 50% 62%, ${RED}30, transparent 70%)` }} />
        {/* hero photo */}
        <img src="/marketing/hero-player.jpg" alt="" className="absolute object-cover" style={{ left: 0, right: 0, top: 470, width: 1080, height: 560, objectPosition: "50% 18%", filter: "saturate(1.15) contrast(1.1)" }} />
        <div className="absolute" style={{ left: 0, right: 0, top: 470, height: 560, background: "linear-gradient(180deg, #0a0a0a 0%, transparent 22%, transparent 62%, #0a0a0a 100%), linear-gradient(90deg, rgba(10,10,10,.85), transparent 30%, transparent 70%, rgba(10,10,10,.85))" }} />
        {/* HUD brackets */}
        {[["top-0 left-0", "border-t-4 border-l-4"], ["top-0 right-0", "border-t-4 border-r-4"], ["bottom-0 left-0", "border-b-4 border-l-4"], ["bottom-0 right-0", "border-b-4 border-r-4"]].map(([pos, b]) => (
          <div key={pos} className={`absolute ${pos} ${b} w-20 h-20 m-8`} style={{ borderColor: `${GOLD}aa` }} />
        ))}

        {/* crest + kicker */}
        <div className="absolute left-0 right-0 flex flex-col items-center" style={{ top: 46 }}>
          <img src="/brand/nexto-academy-logo.png" alt="The Next O Technical Academy" style={{ height: 236, filter: `drop-shadow(0 0 28px ${GOLD}66)` }} />
          <div className="mt-5 flex items-center gap-3 font-mono font-bold uppercase" style={{ fontSize: 17, letterSpacing: "0.42em", color: GOLD }}>
            <span className="h-px w-16" style={{ background: GOLD }} /> Central Illinois' First &amp; Only <span className="h-px w-16" style={{ background: GOLD }} />
          </div>
          <h1 className="font-cover uppercase text-center leading-[0.86] mt-3" style={{ fontSize: 118, textShadow: `0 0 40px ${RED}66` }}>
            Digital <span style={{ color: RED }}>Technical</span><br />Academy
          </h1>
        </div>

        {/* HUD overlay on hero */}
        <div className="absolute" style={{ right: 78, top: 660, width: 240, opacity: 0.95 }}><SkeletalHudMock /></div>
        <div className="absolute flex flex-col gap-2" style={{ left: 80, top: 740 }}>
          {[["Readiness Index", "92", GOLD], ["Weak-Foot Bias", "+9%", "#fff"], ["Scan Status", "Locked", "#22D3EE"]].map(([k, v, c]) => (
            <div key={k} className="flex items-center gap-3 rounded-sm border px-4 py-2 backdrop-blur" style={{ borderColor: `${GOLD}55`, background: "rgba(8,8,8,.7)" }}>
              <span className="font-mono uppercase text-white/55" style={{ fontSize: 12, letterSpacing: "0.3em" }}>{k}</span>
              <span className="font-cover ml-auto" style={{ fontSize: 30, color: c, lineHeight: 1 }}>{v}</span>
            </div>
          ))}
        </div>

        {/* tagline */}
        <div className="absolute left-0 right-0 text-center" style={{ top: 972 }}>
          <div className="font-cover uppercase leading-none" style={{ fontSize: 74 }}>Stop guessing. <span style={{ color: GOLD }}>Start dominating.</span></div>
          <div className="mt-3 font-semibold text-white/70" style={{ fontSize: 21 }}>Video proof · Coach feedback · Central Illinois rankings — one Hub, every rep on record.</div>
        </div>

        {/* five pillars */}
        <div className="absolute left-0 right-0 flex justify-center gap-5 px-14" style={{ top: 1100 }}>
          {PILLARS.map(({ Icon, label, sub }) => (
            <div key={label} className="flex flex-col items-center text-center" style={{ width: 196 }}>
              <div className="w-[76px] h-[76px] rounded-full flex items-center justify-center border-2" style={{ borderColor: GOLD, background: `linear-gradient(160deg, ${GOLD}2e, rgba(10,10,10,.9))`, boxShadow: `0 0 26px ${GOLD}55` }}>
                <Icon size={34} color={GOLD} strokeWidth={2.2} />
              </div>
              <div className="font-headings uppercase mt-3 leading-none whitespace-nowrap" style={{ fontSize: 19, letterSpacing: "0.04em" }}>{label}</div>
              <div className="font-mono uppercase text-white/50 mt-1" style={{ fontSize: 11, letterSpacing: "0.22em" }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* footer bar */}
        <div className="absolute left-0 right-0 bottom-0 flex items-center justify-between px-16" style={{ height: 78, background: RED }}>
          <div className="flex items-center gap-3 font-headings uppercase" style={{ fontSize: 24, letterSpacing: "0.06em" }}><Crown size={20} /> The Next O Technical Academy</div>
          <div className="font-mono font-bold uppercase" style={{ fontSize: 15, letterSpacing: "0.3em" }}>nextosoccer.com · Est. 2026</div>
          <div className="flex items-center gap-2 font-cover" style={{ fontSize: 34 }}><Activity size={20} /> $49 · Volume 1</div>
        </div>
      </div>
    </div>
  );
}
